import { useCallback, useMemo, useRef, useState } from 'react';

interface UseVirtualScrollOptions {
  itemCount: number;
  itemHeight: number;
  containerHeight: number;
}

export function useVirtualScroll({
  itemCount,
  itemHeight,
  containerHeight,
}: UseVirtualScrollOptions) {
  const [scrollTop, setScrollTop] = useState(0);
  const rafRef = useRef<number>();

  const { startIndex, endIndex } = useMemo(() => {
    const overscan = 3;
    const start = Math.max(0, Math.floor(scrollTop / itemHeight) - overscan);
    const visible = Math.ceil(containerHeight / itemHeight);
    const end = Math.min(itemCount - 1, start + visible + overscan * 2);
    return { startIndex: start, endIndex: end };
  }, [scrollTop, itemHeight, containerHeight, itemCount]);

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    // RAF로 쓰로틀링하여 불필요한 리렌더링 방지
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }
    rafRef.current = requestAnimationFrame(() => {
      setScrollTop(target.scrollTop);
    });

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  return {
    startIndex,
    endIndex,
    totalHeight: itemCount * itemHeight,
    handleScroll,
  };
}

interface UseVirtualGridOptions {
  itemCount: number;
  itemHeight: number;
  containerHeight: number;
  columnCount: number;
  gap?: number;
}

export function useVirtualGrid({
  itemCount,
  itemHeight,
  containerHeight,
  columnCount,
  gap = 0,
}: UseVirtualGridOptions) {
  const [scrollTop, setScrollTop] = useState(0);
  const rafRef = useRef<number>();

  const rowHeight = itemHeight + gap;
  const rowCount = Math.ceil(itemCount / columnCount);

  const { startRow, endRow } = useMemo(() => {
    const overscan = 2;
    const start = Math.max(0, Math.floor(scrollTop / rowHeight) - overscan);
    const visible = Math.ceil(containerHeight / rowHeight);
    const end = Math.min(rowCount - 1, start + visible + overscan * 2);
    return { startRow: start, endRow: end };
  }, [scrollTop, rowHeight, containerHeight, rowCount]);

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }
    rafRef.current = requestAnimationFrame(() => {
      setScrollTop(target.scrollTop);
    });

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  return {
    startRow,
    endRow,
    totalHeight: rowCount * rowHeight - gap,
    rowHeight,
    handleScroll,
  };
}
